import { useState, useMemo } from "react";
import { clientKanbanStages, formatMoney, type ClientKanbanCard } from "@/mocks/managerWorkspace";

type StatusFilter = "all" | "active" | "dormant" | "lost" | "noContact" | "noSales";
type SegmentFilter = "all" | "Крупный опт" | "Средний опт" | "Мелкий опт";

// NOTE: industry / lastCommunication aren't on the base ClientKanbanCard type yet.
// Extending locally so this compiles regardless — add these fields to the mock
// data (@/mocks/managerWorkspace) to have them show real values.
type ClientRow = ClientKanbanCard & {
  industry?: string;
  lastCommunication?: string;
};

const statusLabels: Record<StatusFilter, string> = {
  all: "Все статусы",
  active: "Активные",
  dormant: "Уснувшие",
  lost: "Потерянные",
  noContact: "Без контакта",
  noSales: "Без продаж",
};

const statusBadgeClasses: Record<string, string> = {
  active: "bg-[#D4EDDA] text-[#2D6A4F]",
  dormant: "bg-[#FDE68A] text-[#B45309]",
  lost: "bg-[#FEE2E2] text-[#DC2626]",
  noContact: "bg-[#FEF3C7] text-[#D97706]",
  noSales: "bg-[#E8E0D5] text-[#6B5B4D]",
};

const statusDisplayNames: Record<string, string> = {
  active: "Активен",
  dormant: "Уснул",
  lost: "Потерян",
  noContact: "Без контакта",
  noSales: "Без продаж",
};

const segmentBadgeClasses: Record<string, string> = {
  "Крупный опт": "bg-[#FDE68A] text-[#92400E]",
  "Средний опт": "bg-[#D4EDDA] text-[#2D6A4F]",
  "Мелкий опт": "bg-[#FDF2B5] text-[#B45309]",
};

export default function ClientsTableWidget() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [segmentFilter, setSegmentFilter] = useState<SegmentFilter>("all");
  const [showFilters, setShowFilters] = useState(false);

  const allClients: ClientRow[] = useMemo(
    () => clientKanbanStages.flatMap((stage) => stage.cards),
    []
  );

  const totalRevenue = useMemo(
    () => allClients.reduce((sum, c) => sum + c.totalRevenue, 0),
    [allClients]
  );

  const filteredClients = useMemo(() => {
    return allClients.filter((client) => {
      const matchesSearch =
        !searchTerm ||
        client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (client.lastSaleDoc && client.lastSaleDoc.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (client.industry && client.industry.toLowerCase().includes(searchTerm.toLowerCase())) ||
        client.manager.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === "all" || client.status === statusFilter;
      const matchesSegment = segmentFilter === "all" || client.segment === segmentFilter;

      return matchesSearch && matchesStatus && matchesSegment;
    });
  }, [allClients, searchTerm, statusFilter, segmentFilter]);

  const statusCounts = useMemo(() => {
    const counts: Record<StatusFilter, number> = { all: allClients.length, active: 0, dormant: 0, lost: 0, noContact: 0, noSales: 0 };
    allClients.forEach((c) => {
      if (c.status in counts) {
        counts[c.status as keyof typeof counts]++;
      }
    });
    return counts;
  }, [allClients]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (statusFilter !== "all") count++;
    if (segmentFilter !== "all") count++;
    return count;
  }, [statusFilter, segmentFilter]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-4">
          <h2 className="text-[14px] font-semibold text-[#2D2D2D] flex items-center gap-2">
            <i className="ri-group-line text-[15px] text-[#D97706]"></i>
            Клиенты
          </h2>
          <div className="flex items-center gap-3 text-[11px] text-[#666666]">
            <span>Всего: <span className="font-semibold text-[#2D2D2D]">{allClients.length}</span></span>
            <span>Общая выручка: <span className="font-semibold text-[#2D2D2D]">{formatMoney(totalRevenue)}</span></span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <i className="ri-search-line absolute left-2 top-1/2 -translate-y-1/2 text-[11px] text-[#999999]"></i>
            <input
              type="text"
              placeholder="Поиск по клиентам..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-7 pr-3 py-1 text-[11px] border border-[#D5CA8A] rounded bg-white text-[#2D2D2D] placeholder:text-[#999999] focus:outline-none focus:border-[#D97706] w-[200px]"
            />
          </div>
          <div className="relative">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1 px-2 py-1 text-[11px] rounded cursor-pointer transition-colors whitespace-nowrap ${
                showFilters || activeFiltersCount > 0
                  ? "bg-[#FDF2B5] text-[#2D2D2D]"
                  : "text-[#666666] hover:bg-[#FDF2B5]"
              }`}
            >
              <i className="ri-filter-line text-[11px]"></i>
              Фильтр
              {activeFiltersCount > 0 && (
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#D97706] text-white text-[9px] font-bold">
                  {activeFiltersCount}
                </span>
              )}
            </button>
            {showFilters && (
              <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-[#D5CA8A] rounded shadow-lg p-3 min-w-[280px]">
                <div className="mb-3">
                  <span className="text-[10px] font-semibold text-[#666666] mb-1.5 block">Статус клиента</span>
                  <div className="flex flex-wrap gap-1">
                    {(Object.keys(statusLabels) as StatusFilter[]).map((key) => (
                      <button
                        key={key}
                        onClick={() => setStatusFilter(key)}
                        className={`px-2 py-1 text-[10px] rounded cursor-pointer transition-colors whitespace-nowrap ${
                          statusFilter === key
                            ? "bg-[#FDF2B5] text-[#2D2D2D] font-medium"
                            : "text-[#666666] hover:bg-[#FDF2B5]/50"
                        }`}
                      >
                        {statusLabels[key]}
                        {key !== "all" && (
                          <span className="ml-1 text-[#999999]">({statusCounts[key]})</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <span className="text-[10px] font-semibold text-[#666666] mb-1.5 block">Сегмент</span>
                  <div className="flex flex-wrap gap-1">
                    {(["all", "Крупный опт", "Средний опт", "Мелкий опт"] as SegmentFilter[]).map((seg) => (
                      <button
                        key={seg}
                        onClick={() => setSegmentFilter(seg)}
                        className={`px-2 py-1 text-[10px] rounded cursor-pointer transition-colors whitespace-nowrap ${
                          segmentFilter === seg
                            ? "bg-[#FDF2B5] text-[#2D2D2D] font-medium"
                            : "text-[#666666] hover:bg-[#FDF2B5]/50"
                        }`}
                      >
                        {seg === "all" ? "Все сегменты" : seg}
                      </button>
                    ))}
                  </div>
                </div>
                {(statusFilter !== "all" || segmentFilter !== "all") && (
                  <button
                    onClick={() => { setStatusFilter("all"); setSegmentFilter("all"); }}
                    className="mt-2 text-[10px] text-[#DC2626] hover:underline cursor-pointer"
                  >
                    Сбросить фильтры
                  </button>
                )}
              </div>
            )}
          </div>
          <button className="flex items-center gap-1 px-2 py-1 text-[11px] text-[#666666] hover:bg-[#FDF2B5] rounded cursor-pointer transition-colors whitespace-nowrap">
            <i className="ri-add-line text-[11px]"></i>
            Клиент
          </button>
        </div>
      </div>

      {/* Status summary bar */}
      <div className="flex items-center gap-4 mb-3 px-3 py-1.5 bg-[#FFFDF5] border border-[#D5CA8A]/50 rounded text-[10px]">
        {([
          { key: "active" as const, color: "bg-[#2D6A4F]", label: "Активные" },
          { key: "dormant" as const, color: "bg-[#D97706]", label: "Уснувшие" },
          { key: "noContact" as const, color: "bg-[#B45309]", label: "Без контакта" },
          { key: "noSales" as const, color: "bg-[#6B5B4D]", label: "Без продаж" },
          { key: "lost" as const, color: "bg-[#DC2626]", label: "Потерянные" },
        ]).map((s) => (
          <span key={s.key} className="flex items-center gap-1 text-[#666666]">
            <span className={`w-2 h-2 rounded-full ${s.color}`}></span>
            {s.label}: <span className="font-semibold text-[#2D2D2D]">{statusCounts[s.key]}</span>
          </span>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border border-[#D5CA8A] rounded overflow-hidden flex-1 flex flex-col">
        <div className="overflow-x-auto flex-1 flex flex-col">
        {/* Table header */}
        <div className="grid grid-cols-[1fr_120px_130px_90px_150px_100px_90px_110px_80px_90px_90px] gap-2 px-3 py-2 bg-[#FFFDF5] border-b border-[#D5CA8A] text-[10px] font-semibold text-[#666666] min-w-[1330px]">
          <span>Клиент</span>
          <span>Сфера деятельности</span>
          <span>Посл. коммуникация</span>
          <span>Сегмент</span>
          <span>Посл. реализация</span>
          <span>Менеджер</span>
          <span>Посл. заказ</span>
          <span>Выручка</span>
          <span>Заказов</span>
          <span>Статус</span>
          <span>Телефон</span>
        </div>

        {/* Table rows */}
        <div className="max-h-[calc(100vh-320px)] overflow-y-auto min-w-[1330px]">
          {filteredClients.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-[#999999]">
              <i className="ri-search-line text-[24px] mb-2"></i>
              <span className="text-[12px]">Ничего не найдено</span>
              <span className="text-[10px]">Попробуйте изменить параметры поиска или фильтры</span>
            </div>
          ) : (
            filteredClients.map((client) => (
              <div
                key={client.name}
                className="grid grid-cols-[1fr_120px_130px_90px_150px_100px_90px_110px_80px_90px_90px] gap-2 px-3 py-2.5 border-b border-[#E8DB6C]/30 hover:bg-[#FFFDF5] cursor-pointer transition-colors items-center"
              >
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    client.status === "active" ? "bg-[#2D6A4F]" :
                    client.status === "dormant" ? "bg-[#D97706]" :
                    client.status === "noContact" ? "bg-[#B45309]" :
                    client.status === "noSales" ? "bg-[#6B5B4D]" :
                    "bg-[#DC2626]"
                  }`}></div>
                  <span className="text-[12px] text-[#2D2D2D] font-medium truncate">{client.name}</span>
                </div>
                <span className="text-[11px] text-[#666666] truncate">{client.industry || "—"}</span>
                <span className="text-[11px] text-[#666666] truncate">{client.lastCommunication || "—"}</span>
                <span>
                  <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-medium ${segmentBadgeClasses[client.segment] || "bg-[#FDF2B5] text-[#B45309]"}`}>
                    {client.segment}
                  </span>
                </span>
                <span className="text-[11px] text-[#666666] truncate">{client.lastSaleDoc || "—"}</span>
                <span className="text-[11px] text-[#666666]">{client.manager}</span>
                <span className="text-[11px] text-[#2D2D2D]">{client.lastPurchase}</span>
                <span className="text-[11px] font-semibold text-[#2D2D2D]">{formatMoney(client.totalRevenue)}</span>
                <span className="text-[11px] text-[#2D2D2D]">{client.ordersCount}</span>
                <span>
                  <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-medium ${statusBadgeClasses[client.status] || "bg-[#FEF3C7] text-[#D97706]"}`}>
                    {statusDisplayNames[client.status] || client.status}
                  </span>
                </span>
                <span className="text-[10px] text-[#666666]">{client.phone}</span>
              </div>
            ))
          )}
        </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="flex items-center justify-between mt-3 text-[10px] text-[#666666]">
        <span>Показано: {filteredClients.length} из {allClients.length}</span>
        <div className="flex items-center gap-1">
          <button className="w-5 h-5 flex items-center justify-center rounded hover:bg-[#FDF2B5] text-[#999999] cursor-pointer">
            <i className="ri-arrow-left-s-line text-[12px]"></i>
          </button>
          <span className="px-1.5 py-0.5 rounded bg-[#FDF2B5] text-[#2D2D2D] font-medium">1</span>
          <button className="w-5 h-5 flex items-center justify-center rounded hover:bg-[#FDF2B5] text-[#999999] cursor-pointer">
            <i className="ri-arrow-right-s-line text-[12px]"></i>
          </button>
        </div>
      </div>
    </div>
  );
}